/**
 * Patient portal and smart prescription modules.
 */
import { fetchApi, fetchPatientApi , fetchPatientGetFileApi , fetchPatientPostFileApi } from '../client';
import { buildQS } from '../helpers';
import type {
  PatientPortalProfile, PatientAppointmentSlot, PatientAppointmentSummary, PatientSlotDay,
  PatientVisitSummary, PatientPrescriptionSummary, AppointmentStatus,
  PrescriptionTemplate, CreateTemplateRequest, FavoriteMedication,
  AddFavoriteMedicationRequest, CommonDiagnosis, EnhancedPrescriptionCreateRequest,
  Prescription,
} from '../../../types';
import type { RecurringRule } from './appointments';
import type { PagedResponse } from '../../../types/pagination';

/**
 * A patient's view of one lab result. Mirrors the backend's PatientLabSummaryView, which is a
 * deliberately narrower projection than the clinician DTO — no doctorSummary, no confidence, no
 * follow-up. Do not widen this to "share" it with the doctor screens.
 */
export interface PatientLabAiSummary {
  labOrderId: string;
  panel: string | null;
  orderedAt: string | null;
  /** False until a clinician has approved the interpretation. */
  summaryAvailable: boolean;
  /** Plain-language text; present only when summaryAvailable is true. */
  patientSummary: string | null;
  awaitingClinicianReview: boolean;
  disclaimer: string;
}

/** A message in a patient ↔ doctor thread. */
export interface PatientThreadMessage {
  id: string; senderType: 'PATIENT' | 'DOCTOR'; subject?: string; body: string; isRead: boolean; createdAt: string;
}

/** A clinic in the patient directory ("Find care"). */
export interface DirectoryClinic {
  id: string; name: string; city?: string; state?: string; address?: string; phone?: string; logoUrl?: string; distanceKm?: number;
}

/** A doctor in the patient directory ("Find care"). */
export interface DirectoryDoctor {
  id: string; name: string; specialization?: string; opdFee?: number; avatarUrl?: string; bio?: string;
  clinicId: string; clinicName?: string; clinicCity?: string;
}

export interface PatientReferralSummary {
  id: string;
  referralNumber: string;
  referringClinicName: string;
  destinationType: 'CLINVEA' | 'EXTERNAL';
  destinationName: string;
  destinationDoctorName?: string;
  specialty?: string;
  referralReason: string;
  urgency: 'ROUTINE' | 'URGENT' | 'EMERGENCY';
  status: string;
  createdAt: string;
  acceptedAt?: string;
  scheduledAt?: string;
  reportReceivedAt?: string;
  completedAt?: string;
  declinedAt?: string;
  cancelledAt?: string;
  sharedRecordCategories: string[];
}

export interface PatientTransferSummary {
  id: string;
  transferNumber: string;
  initiatingClinicName: string;
  receivingClinicName: string;
  transferType: string;
  reason: string;
  urgency: 'ROUTINE' | 'URGENT' | 'EMERGENCY';
  status: string;
  requestedAt: string;
  acceptedAt?: string;
  departedAt?: string;
  arrivedAt?: string;
  completedAt?: string;
  declinedAt?: string;
  cancelledAt?: string;
  sharedRecordCategories: string[];
}

export interface OtpChallenge {
  challengeId: string;
  expiresInSeconds: number;
  resendAfterSeconds: number;
}

export interface OtpVerifyBody {
  challengeId: string;
  mobile: string;
  code: string;
  doctorSlug: string;
  clinicSlug: string;
}

/**
 * A verified number. `grant` is a short-lived, clinic-scoped permission to book — keep it in memory
 * only (never storage, URLs or analytics). `accountAvailable`: an account bound to this number exists.
 */
export interface OtpVerifyResult {
  status: 'VERIFIED';
  grant: string;
  grantExpiresInSeconds: number;
  accountAvailable: boolean;
}

export type BookingFor = 'SELF' | 'CHILD' | 'PARENT' | 'SPOUSE' | 'OTHER';

export interface PublicBookingBody {
  grant: string;
  mobile: string;
  doctorSlug: string;
  clinicSlug: string;
  date: string;
  time: string;
  visitType: 'NEW' | 'FOLLOWUP';
  /** IN_PERSON (default) or VIDEO — VIDEO only where the affiliation says `videoBooking`. */
  consultationMode?: 'IN_PERSON' | 'VIDEO';
  notes?: string;
  patient: { name: string; gender: string; dateOfBirth: string; bookingFor: BookingFor; guardianConfirmed: boolean; guardianName?: string };
  privacyNoticeAccepted: boolean;
  createAccount?: { email: string; password?: string; termsAccepted: boolean };
  /** How the booker wants the clinic to contact them (doc 84); optional. EMAIL needs contactEmail. */
  contactChannel?: 'WHATSAPP' | 'SMS' | 'EMAIL';
  contactEmail?: string;
}

/** A doctor on the patient's saved list. {@code listed} false: no longer on ClinVea Care (only the name is kept). */
export interface SavedDoctor {
  slug?: string | null; name: string; listed: boolean; specialization?: string | null; photoUrl?: string | null;
  places: { clinicSlug: string; clinicName: string; locality?: string | null; city?: string | null; onlineBooking: boolean }[];
}

/** A lab test a public clinic offers, with its price and the link to book home collection there. */
export interface ClinicLabTest {
  id: string; slug?: string | null; name: string; category?: string | null; sampleType?: string | null;
  fastingRequired: boolean; fastingHours?: string | null; turnaroundHours?: number | null; price: number; bookUrl: string;
  /** GST the clinic adds on its bill (0 = the price is the bill). */
  gstPercent?: number | null;
}

export interface LabCollectionSlot { windowId: string; label: string; remaining: number; available: boolean; reason?: string | null }

/** Home collection at a clinic for a pincode (and a day): whether it's served there, the fee, and the windows. */
export interface LabCollectionAvailability {
  offered: boolean; serviceable: boolean; message?: string | null; collectionFee?: number | null; instructions?: string | null;
  earliestDate?: string | null; latestDate?: string | null; slots: LabCollectionSlot[];
  /** HOME or LAB_VISIT; for a lab visit, where to go. */
  visitMode?: LabVisitMode; labAddress?: string | null;
}

/** HOME: sample collected at the patient's address. LAB_VISIT: the patient visits the lab at a booked slot. */
export type LabVisitMode = 'HOME' | 'LAB_VISIT';
export interface LabBookingModes { homeCollection: boolean; labVisit: boolean }

export interface PublicLabBookingBody {
  grant: string; mobile: string; clinicSlug: string; testIds: string[]; date: string; windowId: string;
  addressLine?: string; landmark?: string; pincode?: string; notes?: string;
  patient: { name: string; gender: string; dateOfBirth: string; bookingFor: BookingFor; guardianConfirmed: boolean; guardianName?: string };
  privacyNoticeAccepted: boolean;
  visitMode: LabVisitMode;
}

export interface PublicLabBookingResult {
  bookingNumber: string; status: string; clinicName: string; clinicPhone?: string | null; date: string; window: string;
  tests: { name: string; price: number }[]; collectionFee: number; total: number;
  visitMode?: LabVisitMode; labAddress?: string | null;
}

/** What the page needs to open Razorpay's checkout for a booking fee (the doctor's own account). */
export interface BookingCheckout {
  orderId: string;
  keyId: string;
  amountPaise: number;
  currency: string;
  amount: number;
  /** The time is held until then; unpaid after it, the request lapses and the time is freed. */
  payBy: string;
  description: string;
}

export interface BookingPaymentOutcome { status: 'AWAITING_PAYMENT' | 'PAID' | 'EXPIRED' | 'REFUND_REQUESTED' | 'REFUNDED' | 'REFUND_FAILED'; amount: number; paidAt?: string | null }

export interface PublicBookingResult {
  /** Present when the doctor asks for payment at booking: the request reaches them once it's paid. */
  payment?: BookingCheckout | null;
  reference: string;
  status: string;
  clinicName: string;
  clinicPhone?: string | null;
  doctorName: string;
  date: string;
  time: string;
  /** On CREATED the session is set as HttpOnly cookies; only native clients get a token (medopd-docs 107). */
  account: { status: 'NOT_REQUESTED' | 'CREATED' | 'FAILED'; message?: string; token?: string; expiresInSeconds?: number; patient?: PatientPortalProfile };
}

/** What the booking screen needs before the patient commits (GET /patient/appointments/booking-options; medopd-docs 136 §7 M1). */
export interface PortalBookingOptions {
  doctorId: string;
  clinicId: string;
  /** The consultation fee (the pay-at-booking amount when that applies, else the doctor's OPD fee); null when unknown. */
  fee?: number | null;
  /** The fee is paid online when booking; the request reaches the clinic once it is paid. */
  payAtBooking: boolean;
  /** This doctor offers video visits here (same rule as the public page). */
  videoBooking: boolean;
  /** The clinic confirms online bookings instantly (no "clinic will confirm" wait). */
  autoConfirm: boolean;
  clinicName?: string | null;
}

/** POST /patient/appointments and /book-external: what actually happened (medopd-docs 136 §7 H1). */
export interface PortalBookingResult {
  appointmentId: string;
  status: AppointmentStatus;
  /** Set when the clinic confirmed instantly. */
  tokenNumber?: number | null;
  /** Present when the doctor asks for payment at booking: the request reaches them once it is paid. */
  payment?: BookingCheckout | null;
  message?: string;
}

/** What the patient can do with an appointment row now (the server re-checks each one when it is taken). */
export type PortalAppointmentAction = 'CANCEL' | 'RESCHEDULE' | 'CHECK_IN' | 'JOIN_VIDEO' | 'RATE';

/** A signed-in patient's appointment row, with the guest page's live queue and the check-in link (medopd-docs 136 §7 M1). */
export type PortalAppointment = PatientAppointmentSummary & {
  queue?: BookingQueue | null;
  /** The pre-visit check-in form, while it is open. */
  checkinUrl?: string | null;
  /** Set while the clinic's check-in form is waiting: the portal opens it at /patient/checkin/{checkinId} (medopd-docs 140). */
  checkinId?: string | null;
  checkinStatus?: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'EXPIRED' | 'BYPASSED' | null;
  // ── medopd-docs 137: rows come from every clinic linked to the login, with how to reach the visit ──
  clinicId?: string | null;
  clinicName?: string | null;
  clinicAddress?: string | null;
  clinicPhone?: string | null;
  /** A maps search link for the clinic (coordinates when set, else its address). */
  clinicMapsUrl?: string | null;
  /** VIDEO, still open: when the join window opens / closes (clinic local time, ISO without zone). */
  videoJoinOpensAt?: string | null;
  videoJoinClosesAt?: string | null;
  /** VIDEO: the patient can press Join right now. */
  videoJoinable?: boolean | null;
  /** VIDEO: this appointment's video page in the portal (needs the portal sign-in; not a credential). */
  videoUrl?: string | null;
  /** Booked without an account: the guest "manage my booking" page for it. */
  manageBookingUrl?: string | null;
  actions?: PortalAppointmentAction[];
};

/**
 * Header that runs ONE call in the appointment's own linked clinic (a row from another clinic linked to this login,
 * medopd-docs 137). The server's PatientTenantContextFilter accepts it only for a clinic linked to the login.
 */
export function inClinic(clinicId?: string | null): RequestInit {
  return clinicId ? { headers: { 'X-Active-Patient-Clinic-Id': clinicId } } : {};
}

/** FULL: the account's own record (the portal can switch into it). APPOINTMENTS: a claimed clinic — appointments only. */
export type ClinicLinkScope = 'FULL' | 'APPOINTMENTS';

export type LinkedClinicRow = {
  clinicId: string;
  clinicName?: string | null;
  city?: string | null;
  primary: boolean;
  accessScope: ClinicLinkScope;
  linkedAt?: string | null;
  canUnlink: boolean;
};

/** A clinic that may hold this person's records: its name and city only — nothing from the record itself. */
export type ClinicLinkSuggestion = { clinicId: string; clinicName?: string | null; city?: string | null };

export type ClinicLinksOverview = {
  mobileVerified: boolean;
  noticeVersion: string;
  linked: LinkedClinicRow[];
  suggestions: ClinicLinkSuggestion[];
};

/** What the patient agreed to: the (unticked by default) box, the notice version on screen and its language. */
export type ClinicLinkConsent = { consent: boolean; noticeVersion: string; language?: string };

export type ClinicLinkCodeSent = {
  challengeId: string;
  /** e.g. "••••••3210" — the number on that clinic's record, never shown in full. */
  maskedMobile: string;
  expiresInSeconds: number;
  resendAfterSeconds: number;
};

/** A guest booking as the manage page sees it (no internal ids; the reference is the handle). */
export interface BookingQueue {
  tokenNumber: number;
  /** Confirmed tokens before this one; an in-progress consultation counts as one. */
  ahead: number;
  nowServing?: number | null;
  /** HH:mm (clinic time) — ahead × the doctor's consultation length, floored at the booked time. */
  estimatedStart?: string | null;
  consultMinutes: number;
  yourTurn: boolean;
}

export interface GuestLabResultRow { name: string; value?: string | null; unit?: string | null; referenceRange?: string | null; classification?: string | null; critical: boolean }
export interface GuestLabOrder { id: string | null; testName: string; status: string; released: boolean; reportAvailable: boolean; results: GuestLabResultRow[] }
export interface GuestLabResults {
  bookingNumber: string; clinicName?: string | null; clinicPhone?: string | null; status?: string | null; date?: string | null;
  visitMode?: 'HOME' | 'LAB_VISIT' | null; orders: GuestLabOrder[];
}

/** GET /api/patient/ai-summary (medopd-docs 139): sections built from records, plus the written brief (markdown). */
export interface PatientAiSummary {
  preparedAt: string;
  profile: { name?: string | null; age?: number | null; gender?: string | null; bloodGroup?: string | null };
  conditions: string[];
  allergies: string[];
  lastSeen?: string | null;
  daysSinceLastSeen?: number | null;
  recentVisits: { date: string | null; doctorName?: string | null }[];
  activeMedications: { name: string; dose?: string | null; frequency?: string | null; duration?: string | null }[];
  labFindings: { labOrderId: string; testName: string; date?: string | null; explanation?: string | null;
    flags: { name?: string | null; value?: string | null; unit?: string | null; flag: string }[] }[];
  awaitingResults: { labOrderId: string; testName: string; stage: string }[];
  writtenBrief?: string | null;
}

export interface PatientAiSummaryConsentStatus { granted: boolean; noticeVersion: string; decidedAt?: string | null }

/** GET /api/patient/checkins/pending row (medopd-docs 140): a check-in form the clinic sent that is still waiting. */
export interface PatientPendingCheckin {
  checkinId: string;
  appointmentId: string;
  date?: string | null;
  time?: string | null;
  doctorName?: string | null;
  status: string;
  sentAt?: string | null;
  expiresAt?: string | null;
}

/** GET /api/patient/visit-records/{id}/summary (medopd-docs 140): what happened at one visit. */
export interface PatientVisitSummaryDetail {
  id: string;
  date: string | null;
  reason?: string | null;
  clinicName?: string | null;
  doctorName?: string | null;
  doctorSpecialization?: string | null;
  mode: string;
  tokenNumber?: number | null;
  vitals: { bp?: string | null; pulse?: number | null; temperature?: number | null; spo2?: number | null; weight?: number | null; respiratoryRate?: number | null } | null;
  diagnosis?: string | null;
  prescription: { id: string; issuedAt?: string | null; pdfAvailable?: boolean;
    medicines: { name: string; dose?: string | null; frequency?: string | null; duration?: string | null; notes?: string | null }[] } | null;
  followUpDays?: number | null;
  followUpDate?: string | null;
  summary: { diagnosisSummary?: string | null; followUpPlan?: string | null } | null;
  labTests: { labOrderId: string; testName: string; stage: string; ready: boolean }[];
  bill: { id: string; billNumber?: string | null; discount?: number | null; gst?: number | null; insuranceCovered?: number | null; total: number; paid: number; due: number; status?: string | null;
    items: { name: string; quantity?: number | null; amount?: number | null }[] } | null;
  timeline: { type: string; at: string }[];
}

/** GET /api/patient/visit-records row: a visit, its issued prescription (if any) and the approved summary (if any). */
export interface PatientVisitRecord {
  id: string;
  date: string | null;
  doctorName: string | null;
  reason?: string | null;
  prescription: PatientPrescriptionSummary | null;
  summary: { diagnosisSummary?: string | null; followUpPlan?: string | null } | null;
}

export interface PatientVisitSummaryApproved {
  id: string; visitId?: string | null; date?: string | null; diagnosisSummary: string; followUpPlan: string; medicationsPrescribed: string;
}

export interface ManagedBooking {
  reference: string;
  status: 'REQUESTED' | 'WAITING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED' | 'DECLINED' | 'EXPIRED' | 'SKIPPED' | 'NO_SHOW' | 'UNKNOWN';
  bookedFor: BookingFor;
  patientFirstName?: string | null;
  doctorName?: string | null;
  clinicName?: string | null;
  clinicPhone?: string | null;
  date?: string | null;
  time?: string | null;
  doctorSlug?: string | null;
  clinicSlug?: string | null;
  canCancel: boolean;
  canReschedule: boolean;
  changeBlockedReason?: string | null;
  cutoffHours: number;
  rescheduleCount: number;
  /** Today's live queue for this booking (confirmed/in-progress only): counts, never other patients' details. */
  queue?: BookingQueue | null;
  /** IN_PERSON (default) or VIDEO. */
  consultationMode?: 'IN_PERSON' | 'VIDEO';
}

export interface PublicDoctorAffiliation {
  clinicSlug: string; clinicName: string; locality?: string | null; city?: string | null;
  address?: string | null; fee?: number | null; onlineBooking: boolean;
  /** Paid online when booking here; null when nothing is paid up front. */
  payAtBooking?: number | null;
  /** Patients can book a video visit with this doctor here. */
  videoBooking?: boolean;
}

export interface PublicDoctorPage {
  slug: string; displayName: string; specialization?: string | null; yearsOfExperience?: number | null;
  languages?: string[]; verified: boolean; affiliations: PublicDoctorAffiliation[];
  /** Absolute URL of the approved portrait on the public site, or null. */
  photoUrl?: string | null;
}

export interface PublicSlot { date: string; start: string; end: string }

/** A doctor's suggested new time, as the patient's link page shows it. */
export interface ChangeProposalView {
  status: 'PENDING' | 'ACCEPTED' | 'DECLINED' | 'EXPIRED' | 'WITHDRAWN';
  patientFirstName?: string | null;
  doctorName: string;
  clinicName?: string | null;
  clinicPhone?: string | null;
  currentWhen?: string | null;
  proposedWhen: string;
  note?: string | null;
  answerBy: string;
  canAnswer: boolean;
}

export interface ChangeAnswer {
  status: string;
  newWhen: string;
  tokenNumber?: number | null;
  confirmationSent?: { email: string; sms: string } | null;
}

export interface PendingProposal { id: string; appointmentId: string; proposedWhen: string; note?: string | null; answerBy: string }

export const patientPortalModule = {
  /** Answering a doctor's suggested new time — by link (no sign-in) or from the signed-in appointments list. */
  appointmentChange: {
    view: (token: string) => fetchPatientApi<ChangeProposalView>(`/api/public/appointment-change/${encodeURIComponent(token)}`),
    accept: (token: string) =>
      fetchPatientApi<ChangeAnswer>(`/api/public/appointment-change/${encodeURIComponent(token)}/accept`, { method: 'POST' }),
    decline: (token: string) =>
      fetchPatientApi<ChangeAnswer>(`/api/public/appointment-change/${encodeURIComponent(token)}/decline`, { method: 'POST' }),
    pending: () => fetchPatientApi<PendingProposal[]>('/api/patient/appointment-proposals'),
    acceptMine: (id: string) =>
      fetchPatientApi<ChangeAnswer>(`/api/patient/appointment-proposals/${encodeURIComponent(id)}/accept`, { method: 'POST' }),
    declineMine: (id: string) =>
      fetchPatientApi<ChangeAnswer>(`/api/patient/appointment-proposals/${encodeURIComponent(id)}/decline`, { method: 'POST' }),
  },

  auth: {
    /** Saves the portal language on the signed-in patient's account (medopd-docs 106). */
    setMyLanguage: (language: string) =>
      fetchPatientApi<{ preferredLanguage: string }>('/api/patient/auth/me/language', { method: 'PUT', body: JSON.stringify({ language }) }),
    login: (email: string, password: string) =>
      fetchPatientApi<{ expiresInSeconds?: number; patient: PatientPortalProfile }>('/api/patient/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      }),
    getCurrentPatient: () =>
      fetchPatientApi<{ patient: PatientPortalProfile }>('/api/patient/auth/me'),
    /** Sends a 6-digit sign-in code to an Indian mobile number (anonymous). In local/dev the code
     *  is fixed server-side and no SMS is sent; it is never returned to the client. */
    otpRequest: (mobile: string) =>
      fetchPatientApi<OtpChallenge>('/api/patient/auth/otp/request', {
        method: 'POST',
        body: JSON.stringify({ mobile }),
      }),
    /** Checks the code; signs in, asks which account (shared family number), asks for sign-up
     *  details (new number), or — with profile — creates the account. */
    otpVerify: (body: OtpVerifyBody) =>
      fetchPatientApi<OtpVerifyResult>('/api/patient/auth/otp/verify', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    /** Optional: open the ClinVea account bound to the verified number (never one chosen by the client). */
    otpSession: (grant: string, doctorSlug: string, clinicSlug: string) =>
      fetchPatientApi<{ status: 'SIGNED_IN'; expiresInSeconds?: number; patient: PatientPortalProfile }>('/api/patient/auth/otp/session', {
        method: 'POST',
        body: JSON.stringify({ grant, doctorSlug, clinicSlug }),
      }),
    /** "Sign in with SMS code" on /patient/login (medopd-docs 136 §7 M3): a code to the number; same per-number quotas as every OTP. */
    otpSignInRequest: (mobile: string) =>
      fetchPatientApi<OtpChallenge>('/api/public/patient/auth/otp/request', { method: 'POST', body: JSON.stringify({ mobile }) }),
    /** Opens the one account bound to the verified number (NO_ACCOUNT when none is: use email, then bind the number in Profile). */
    otpSignInVerify: (challengeId: string, mobile: string, code: string) =>
      fetchPatientApi<{ status: 'SIGNED_IN'; expiresInSeconds?: number; patient: PatientPortalProfile }>('/api/public/patient/auth/otp/verify', {
        method: 'POST',
        body: JSON.stringify({ challengeId, mobile, code }),
      }),
    /** Exchanges the httpOnly patient_refresh_token cookie for a new access token — mirrors
     *  authModule.refresh() on the staff side. */
    refresh: () =>
      fetchPatientApi<{ expiresInSeconds: number }>('/api/patient/auth/refresh', { method: 'POST' }),
    logout: () =>
      fetchPatientApi<{ message: string }>('/api/patient/auth/logout', { method: 'POST' }),
    changePassword: (currentPassword: string, newPassword: string) =>
      fetchPatientApi<{ success: boolean }>('/api/patient/auth/password/change', {
        method: 'POST',
        body: JSON.stringify({ currentPassword, newPassword }),
      }),
    /**
     * Patient accounts live in a separate table from staff (PatientAccount, not User) with their
     * own backend lookup — the staff /api/auth/password/forgot endpoint never finds a patient's
     * email, so this must hit the patient-specific endpoint, not authModule.requestPasswordReset.
     * redirectUrl is required server-side (RedirectUrlValidator) — it's what gets emailed as the
     * reset link, so it must point at the PATIENT reset page, not the staff one.
     */
    forgotPassword: (email: string) =>
      fetchPatientApi<{ message: string }>('/api/patient/auth/password/forgot', {
        method: 'POST',
        skipAuth: true,
        body: JSON.stringify({ email, redirectUrl: `${window.location.origin}/patient/reset-password` }),
      }),
    /** No separate verify-token step on the patient side (unlike staff) — the reset call itself
     *  validates the token and reports invalid/expired inline. */
    resetPassword: (token: string, password: string) =>
      fetchPatientApi<{ message: string }>('/api/patient/auth/password/reset', {
        method: 'POST',
        skipAuth: true,
        body: JSON.stringify({ token, password }),
      }),
    /** Re-consent path for an already-registered patient whose account predates this version of
     *  the Patient Portal Agreement (or predates acceptance tracking existing at all). Registration
     *  logs acceptance once at account creation; this is the equivalent for an existing session,
     *  driven by PatientPortalProfile.termsAcceptanceRequired. */
    acceptCurrentTerms: () =>
      fetchPatientApi<{ termsVersion: string }>('/api/patient/auth/terms/accept', { method: 'POST' }),
    verifyInvitation: (token: string) =>
      fetchPatientApi<{ valid: boolean; email?: string; patientId?: string; expiresAt?: string }>(
        '/api/patient/auth/invitation/verify',
        { method: 'POST', skipAuth: true, body: JSON.stringify({ token }) }
      ),
    /** Self-service resend when the registration link has expired — the (expired) token proves receipt of the original email. */
    resendInvitation: (token: string, redirectUrl: string) =>
      fetchPatientApi<{ message: string; email: string; expiresAt: string }>(
        '/api/patient/auth/invitation/resend',
        { method: 'POST', skipAuth: true, body: JSON.stringify({ token, redirectUrl }) }
      ),
    register: (data: {
      token: string; password: string; name?: string; email?: string; mobile?: string;
      dateOfBirth?: string; gender?: string; termsAcceptedAt?: string;
      termsIpAddress?: string; termsUserAgent?: string; termsVersion?: string;
    }) =>
      fetchPatientApi<{ token: string; patient: PatientPortalProfile }>('/api/patient/auth/register', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  },

  profile: {
    get: () => fetchPatientApi<PatientPortalProfile>('/api/patient/profile'),
    update: (data: Partial<Pick<PatientPortalProfile, 'name' | 'mobile' | 'dateOfBirth' | 'gender'>>) =>
      fetchPatientApi<{ success: boolean }>('/api/patient/profile', {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    /** Bind (or change) the mobile number that can sign this account in by SMS code (medopd-docs 136 §7 M3). */
    mobileSignIn: {
      requestCode: (mobile: string) =>
        fetchPatientApi<OtpChallenge>('/api/patient/profile/mobile/request-code', { method: 'POST', body: JSON.stringify({ mobile }) }),
      verify: (challengeId: string, mobile: string, code: string) =>
        fetchPatientApi<{ mobileSignInEnabled: boolean }>('/api/patient/profile/mobile/verify', {
          method: 'POST', body: JSON.stringify({ challengeId, mobile, code }) }),
    },
    /** Promotional messages from the patient's clinic. Reminders and results are never affected. */
    setMarketingConsent: (optIn: boolean) =>
      fetchPatientApi<{ marketingConsent: boolean }>('/api/patient/profile/marketing-consent', {
        method: 'PUT',
        body: JSON.stringify({ optIn }),
      }),
  },

  /** One-click unsubscribe from a clinic's promotional email; the signed token in the link is the only credential. */
  marketing: {
    unsubscribe: (token: string) =>
      fetchPatientApi<{ unsubscribed: boolean }>('/api/public/marketing/unsubscribe', {
        method: 'POST', skipAuth: true, body: JSON.stringify({ token }),
      }),
  },

  vitals: {
    /** `from`/`to` are ISO date strings (YYYY-MM-DD) — the backend already supports range filtering. */
    get: (patientId: string, visitId?: string, from?: string, to?: string) => {
      const params = new URLSearchParams({ patientId });
      if (visitId) params.append('visitId', visitId);
      if (from) params.append('from', from);
      if (to) params.append('to', to);
      return fetchPatientApi<any[]>(`/api/patient/vitals?${params.toString()}`);
    },
    /** One page of readings, newest first (medopd-docs 112); optional YYYY-MM-DD range. */
    getPage: (page: number, size: number, from?: string, to?: string) =>
      fetchPatientApi<PagedResponse<any>>(`/api/patient/vitals${buildQS({ from, to, page, size })}`),
  },

  doctors: {
    getAll: () =>
      fetchPatientApi<Array<{ id: string; name: string; specialization?: string }>>('/api/patient/doctors'),
  },

  /** Read-only history — see PatientReferralTransferHistoryService's class comment on the backend
   *  for the cross-clinic-identity scope limitation (only shows referrals/transfers from clinics
   *  this login is already linked to). */
  referralHistory: {
    list: () => fetchPatientApi<PatientReferralSummary[]>('/api/patient/referrals'),
    /** One page, newest first (medopd-docs 117). */
    listPage: (page: number, size: number) =>
      fetchPatientApi<PagedResponse<PatientReferralSummary>>(`/api/patient/referrals${buildQS({ page, size })}`),
  },
  transferHistory: {
    list: () => fetchPatientApi<PatientTransferSummary[]>('/api/patient/transfers'),
    /** One page, newest first (medopd-docs 117). */
    listPage: (page: number, size: number) =>
      fetchPatientApi<PagedResponse<PatientTransferSummary>>(`/api/patient/transfers${buildQS({ page, size })}`),
  },

  /** The patient's own recurring follow-up plans at the current clinic (read-only; set up by the clinic). */
  recurringAppointments: {
    list: () => fetchPatientApi<RecurringRule[]>('/api/patient/recurring-appointments'),
  },

  appointments: {
    getSlots: (doctorId: string, date: string) =>
      fetchPatientApi<PatientAppointmentSlot[]>(
        `/api/patient/appointments/slots?${new URLSearchParams({ doctorId, date }).toString()}`
      ),
    /** Slots for each of the next `days` days (max 21) in one call — powers the booking date strip. */
    getSlotRange: (doctorId: string, from: string, days = 14, clinicId?: string | null) =>
      fetchPatientApi<PatientSlotDay[]>(
        `/api/patient/appointments/slots/range?${new URLSearchParams({ doctorId, from, days: String(days) }).toString()}`,
        inClinic(clinicId)
      ),
    /** Fee, pay-at-booking, video and instant-confirm facts for a doctor (own clinic or one found via Find Care). */
    bookingOptions: (doctorId: string, clinicId?: string | null) =>
      fetchPatientApi<PortalBookingOptions>(
        `/api/patient/appointments/booking-options?${new URLSearchParams({ doctorId, ...(clinicId ? { clinicId } : {}) }).toString()}`
      ),
    book: (data: { doctorId: string; date: string; time: string; visitType: 'NEW' | 'FOLLOWUP'; notes?: string; insurancePolicyId?: string;
      consultationMode?: 'IN_PERSON' | 'VIDEO' }) =>
      fetchPatientApi<PortalBookingResult>(
        '/api/patient/appointments',
        { method: 'POST', body: JSON.stringify(data) }
      ),
    list: () => fetchPatientApi<PortalAppointment[]>('/api/patient/appointments'),
    /** Still-actionable appointments from today on, soonest first — short, never paged (medopd-docs 112). */
    listUpcoming: () => fetchPatientApi<PortalAppointment[]>('/api/patient/appointments?scope=upcoming'),
    /** Everything else, newest first, one page at a time (medopd-docs 112). */
    listPast: (page: number, size: number) =>
      fetchPatientApi<PagedResponse<PatientAppointmentSummary>>(`/api/patient/appointments${buildQS({ scope: 'past', page, size })}`),
    /** `clinicId`: the row's own linked clinic (rows can come from any clinic linked to the login, medopd-docs 137). */
    cancel: (id: string, clinicId?: string | null) =>
      fetchPatientApi<{ success: boolean }>(`/api/patient/appointments/${id}/cancel`, {
        method: 'PATCH', ...inClinic(clinicId),
      }),
    /** Opens the feedback form for one of the patient's own completed visits (returns the form's link token). */
    feedbackLink: (id: string, clinicId?: string | null) =>
      fetchPatientApi<{ feedbackToken: string }>(`/api/patient/appointments/${encodeURIComponent(id)}/feedback-link`, {
        method: 'POST', ...inClinic(clinicId),
      }),
    /** Move an appointment to another time with the same doctor; the new time is a request the clinic confirms. */
    reschedule: (id: string, date: string, time: string, clinicId?: string | null) =>
      fetchPatientApi<{ appointmentId: string; status: AppointmentStatus; tokenNumber?: number | null; message: string }>(
        `/api/patient/appointments/${id}/reschedule`,
        { method: 'PATCH', body: JSON.stringify({ date, time }), ...inClinic(clinicId) }
      ),
    /** Request an appointment at a clinic not yet linked to this login — found via directory search.
     *  Like book(), this submits a REQUEST the clinic confirms (no instant token). */
    bookExternal: (data: {
      targetClinicId: string; doctorId: string; date: string; time: string;
      visitType: 'NEW' | 'FOLLOWUP'; notes?: string; consultationMode?: 'IN_PERSON' | 'VIDEO';
    }) =>
      fetchPatientApi<PortalBookingResult>(
        '/api/patient/appointments/book-external',
        { method: 'POST', body: JSON.stringify(data) }
      ),
  },

  /** The patient's own bills (the list itself is read by PatientBills straight from /api/patient/bills). */
  bills: {
    /** The patient's copy of one bill as a PDF (medopd-docs 136 §7 M5); 404 for a bill that isn't theirs. */
    pdf: (billId: string) => fetchPatientGetFileApi(`/api/patient/bills/${encodeURIComponent(billId)}/pdf`),
  },

  /** Clinics this login has ever booked with (primary clinic first) + booking at a new one. */
  clinics: {
    getLinked: () =>
      fetchPatientApi<Array<{ clinicId: string; clinicName?: string; clinicLogoUrl?: string; isPrimary: boolean;
        accessScope?: ClinicLinkScope; switchable?: boolean }>>(
        '/api/patient/clinics'
      ),
  },

  /**
   * "Linked clinics" (medopd-docs 137): which clinics are linked, which may hold the patient's records (clinic name and
   * city only), and the explicit claim — notice + consent tick, then a code sent to the number on THAT clinic's record.
   * Unlinking is one call. Nothing is ever linked without these steps.
   */
  /** Check-in forms the clinic sent, filled inside the portal (medopd-docs 140). */
  checkins: {
    pending: () => fetchPatientApi<PatientPendingCheckin[]>('/api/patient/checkins/pending'),
    open: (checkinId: string) =>
      fetchPatientApi<{ checkinId: string; token: string }>(`/api/patient/checkins/${encodeURIComponent(checkinId)}/open`, { method: 'POST' }),
  },

  /** AI Health Summary (medopd-docs 139): consent once per notice version, then the summary. */
  aiSummary: {
    consentStatus: () => fetchPatientApi<PatientAiSummaryConsentStatus>('/api/patient/ai-summary/consent'),
    grant: (noticeVersion: string, language: string) =>
      fetchPatientApi<{ granted: boolean }>('/api/patient/ai-summary/consent', {
        method: 'POST', body: JSON.stringify({ consent: true, noticeVersion, language }) }),
    withdraw: (language: string) =>
      fetchPatientApi<{ granted: boolean }>(`/api/patient/ai-summary/consent?language=${encodeURIComponent(language)}`, { method: 'DELETE' }),
    get: () => fetchPatientApi<PatientAiSummary>('/api/patient/ai-summary'),
  },

  clinicLinks: {
    overview: () => fetchPatientApi<ClinicLinksOverview>('/api/patient/clinic-links'),
    requestCode: (clinicId: string, consent: ClinicLinkConsent) =>
      fetchPatientApi<ClinicLinkCodeSent>(`/api/patient/clinic-links/${encodeURIComponent(clinicId)}/request-code`, {
        method: 'POST', body: JSON.stringify(consent) }),
    verify: (clinicId: string, consent: ClinicLinkConsent, challengeId: string, code: string) =>
      fetchPatientApi<{ clinicId: string; clinicName?: string | null }>(`/api/patient/clinic-links/${encodeURIComponent(clinicId)}/verify`, {
        method: 'POST', body: JSON.stringify({ ...consent, challengeId, code }) }),
    unlink: (clinicId: string, language?: string) =>
      fetchPatientApi<{ unlinked: boolean; clinicId: string }>(
        `/api/patient/clinic-links/${encodeURIComponent(clinicId)}${language ? `?language=${encodeURIComponent(language)}` : ''}`,
        { method: 'DELETE' }),
  },

  /** Cross-clinic search — any clinic/doctor on the platform, not just the patient's home clinic. */
  directory: {
    searchClinics: (params: { q?: string; lat?: number; lng?: number; radiusKm?: number }) => {
      const qs = new URLSearchParams();
      if (params.q) qs.append('q', params.q);
      if (params.lat != null) qs.append('lat', String(params.lat));
      if (params.lng != null) qs.append('lng', String(params.lng));
      if (params.radiusKm != null) qs.append('radiusKm', String(params.radiusKm));
      return fetchPatientApi<Array<{
        id: string; name: string; city?: string; state?: string; address?: string;
        phone?: string; logoUrl?: string; distanceKm?: number;
      }>>(`/api/patient/directory/clinics?${qs.toString()}`);
    },
    /** One page of clinics: nearest first when a location is given, then by name (medopd-docs 117). */
    searchClinicsPage: (params: { q?: string; lat?: number; lng?: number; radiusKm?: number }, page: number, size: number) =>
      fetchPatientApi<PagedResponse<DirectoryClinic>>(`/api/patient/directory/clinics${buildQS({ ...params, page, size })}`),
    /** One page of doctors, by name (medopd-docs 117). */
    searchDoctorsPage: (params: { q?: string; clinicId?: string }, page: number, size: number) =>
      fetchPatientApi<PagedResponse<DirectoryDoctor>>(`/api/patient/directory/doctors${buildQS({ ...params, page, size })}`),
    searchDoctors: (params: { q?: string; clinicId?: string }) => {
      const qs = new URLSearchParams();
      if (params.q) qs.append('q', params.q);
      if (params.clinicId) qs.append('clinicId', params.clinicId);
      return fetchPatientApi<Array<{
        id: string; name: string; specialization?: string; opdFee?: number;
        avatarUrl?: string; bio?: string; clinicId: string; clinicName?: string; clinicCity?: string;
      }>>(`/api/patient/directory/doctors?${qs.toString()}`);
    },
    /** A single doctor's public profile — targeted lookup, avoids downloading a whole clinic's list. */
    getDoctor: (doctorId: string) =>
      fetchPatientApi<{
        id: string; name: string; specialization?: string; opdFee?: number;
        clinicId: string; clinicName?: string; clinicCity?: string;
      }>(`/api/patient/directory/doctors/${doctorId}`),
    /** Slots for a doctor at any clinic — resolves clinic from the doctor, not the caller's active clinic. */
    getDoctorSlots: (doctorId: string, date: string) =>
      fetchPatientApi<Array<{ startTime: string; endTime: string }>>(
        `/api/patient/directory/doctors/${doctorId}/slots?date=${date}`
      ),
    /** 14-day availability for a doctor at any clinic (external booking). */
    getDoctorSlotRange: (doctorId: string, from: string, days = 14) =>
      fetchPatientApi<PatientSlotDay[]>(
        `/api/patient/directory/doctors/${doctorId}/slots/range?${new URLSearchParams({ from, days: String(days) }).toString()}`
      ),
    /**
     * Booking hand-off from a public page (care.clinvea.com): public slugs → the ids the booking flow
     * uses. Fails while the doctor is not publicly listed at that clinic.
     */
    resolvePublicListing: (doctorSlug: string, clinicSlug: string) =>
      fetchPatientApi<{ doctorId: string; clinicId: string; doctorName: string }>(
        `/api/patient/directory/resolve?${new URLSearchParams({ doctorSlug, clinicSlug }).toString()}`
      ),
    /** Anonymous public directory reads used by the booking page before sign-in (slugs only). */
    publicDoctor: (doctorSlug: string) =>
      fetchPatientApi<PublicDoctorPage>(`/api/public/directory/doctors/${encodeURIComponent(doctorSlug)}`),
    /** {@code mode} VIDEO: only times outside the clinic's (longer) video notice. */
    publicAvailability: (doctorSlug: string, clinicSlug: string, date: string, mode: 'IN_PERSON' | 'VIDEO' = 'IN_PERSON') =>
      fetchPatientApi<PublicSlot[]>(
        `/api/public/directory/doctors/${encodeURIComponent(doctorSlug)}/availability?${new URLSearchParams({ clinic: clinicSlug, date, mode }).toString()}`
      ),
  },

  /** Appointment requests from the public booking page without an account (authorised by the grant). */
  /** The signed-in patient's saved doctors (kept on the account). */
  savedDoctors: {
    list: () => fetchPatientApi<SavedDoctor[]>('/api/patient/saved-doctors'),
    save: (slug: string) => fetchPatientApi<SavedDoctor>(`/api/patient/saved-doctors/${encodeURIComponent(slug)}`, { method: 'PUT' }),
    remove: (slug: string) => fetchPatientApi<{ removed: boolean }>(`/api/patient/saved-doctors/${encodeURIComponent(slug)}`, { method: 'DELETE' }),
  },

  /** Lab tests on ClinVea Care: a clinic's list, and booking home sample collection without an account. */
  labs: {
    clinicTests: (clinicSlug: string) =>
      fetchPatientApi<ClinicLabTest[]>(`/api/public/directory/clinics/${encodeURIComponent(clinicSlug)}/lab-tests`),
    /** Which kinds of lab booking the clinic takes online. */
    modes: (clinicSlug: string) =>
      fetchPatientApi<LabBookingModes>(`/api/public/lab-booking/${encodeURIComponent(clinicSlug)}/modes`),
    availability: (clinicSlug: string, pincode: string, date?: string, mode: LabVisitMode = 'HOME') =>
      fetchPatientApi<LabCollectionAvailability>(`/api/public/lab-booking/${encodeURIComponent(clinicSlug)}/availability?${new URLSearchParams({
        ...(mode === 'HOME' ? { pincode } : {}), ...(date ? { date } : {}), mode }).toString()}`),
    verify: (challengeId: string, mobile: string, code: string, clinicSlug: string) =>
      fetchPatientApi<{ grant: string; grantExpiresInSeconds: number }>('/api/public/lab-booking/otp/verify',
        { method: 'POST', body: JSON.stringify({ challengeId, mobile, code, clinicSlug }) }),
    /** {@code idempotencyKey}: the same key on a retry returns the booking already made instead of a second one. */
    book: (body: PublicLabBookingBody, idempotencyKey?: string) =>
      fetchPatientApi<PublicLabBookingResult>('/api/public/lab-booking/requests', {
        method: 'POST', body: JSON.stringify(body), ...(idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {}),
      }),
  },

  publicBooking: {
    /** Razorpay's signed checkout response for a booking fee. The signature is the proof; no session needed. */
    verifyPayment: (body: { razorpayOrderId: string; razorpayPaymentId: string; razorpaySignature: string }) =>
      fetchPatientApi<BookingPaymentOutcome>('/api/public/booking/payments/verify', { method: 'POST', body: JSON.stringify(body) }),
    /** {@code idempotencyKey}: the same key on a retry returns the request already made instead of a second one. */
    request: (body: PublicBookingBody, idempotencyKey?: string) =>
      fetchPatientApi<PublicBookingResult>('/api/public/booking/requests', {
        method: 'POST',
        body: JSON.stringify(body),
        ...(idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {}),
      }),
    /** "Manage my booking" without an account: reference + SMS code, then a short-lived grant. */
    manage: {
      verify: (challengeId: string, mobile: string, code: string, reference: string) =>
        fetchPatientApi<{ grant: string; grantExpiresInSeconds: number; booking: ManagedBooking }>(
          '/api/public/booking/manage/verify', { method: 'POST', body: JSON.stringify({ challengeId, mobile, code, reference }) }),
      view: (grant: string, reference: string) =>
        fetchPatientApi<ManagedBooking>('/api/public/booking/manage/view', { method: 'POST', body: JSON.stringify({ grant, reference }) }),
      cancel: (grant: string, reference: string) =>
        fetchPatientApi<ManagedBooking>('/api/public/booking/manage/cancel', { method: 'POST', body: JSON.stringify({ grant, reference }) }),
      reschedule: (grant: string, reference: string, date: string, time: string) =>
        fetchPatientApi<ManagedBooking>('/api/public/booking/manage/reschedule', {
          method: 'POST', body: JSON.stringify({ grant, reference, date, time }) }),
    },
    /** A guest's lab results: booking number + the booking's mobile + a code (PublicLabResultsController). */
    labResults: {
      verify: (challengeId: string, mobile: string, code: string, bookingNumber: string) =>
        fetchPatientApi<{ grant: string; grantExpiresInSeconds: number; results: GuestLabResults }>(
          '/api/public/lab-results/verify', { method: 'POST', body: JSON.stringify({ challengeId, mobile, code, bookingNumber }) }),
      view: (grant: string, bookingNumber: string) =>
        fetchPatientApi<GuestLabResults>('/api/public/lab-results/view', { method: 'POST', body: JSON.stringify({ grant, bookingNumber }) }),
      report: (grant: string, bookingNumber: string, orderId: string) =>
        fetchPatientPostFileApi('/api/public/lab-results/report', { grant, bookingNumber, orderId }),
    },
  },

  /** Messaging with a doctor — restricted server-side to doctors the patient has real appointment history with. */
  messages: {
    getEligibleDoctors: () =>
      fetchPatientApi<Array<{ doctorId: string; name: string; specialization?: string; unreadCount: number }>>(
        '/api/patient/messages/doctors'
      ),
    /** Lightweight total unread count for the portal badge — one COUNT query, not the doctor list. */
    unreadCount: () =>
      fetchPatientApi<{ count: number }>('/api/patient/messages/unread-count'),
    getThread: (doctorId: string) =>
      fetchPatientApi<Array<{
        id: string; senderType: 'PATIENT' | 'DOCTOR'; subject?: string; body: string;
        isRead: boolean; createdAt: string;
      }>>(`/api/patient/messages/thread/${doctorId}`),
    /** One page of a thread, NEWEST first: page 0 is the latest messages, later pages go back in time (medopd-docs 117). */
    getThreadPage: (doctorId: string, page: number, size: number) =>
      fetchPatientApi<PagedResponse<PatientThreadMessage>>(`/api/patient/messages/thread/${doctorId}${buildQS({ page, size })}`),
    send: (doctorId: string, data: { subject?: string; body: string }) =>
      fetchPatientApi<{ id: string }>(`/api/patient/messages/thread/${doctorId}`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  },

  /** Family/dependent accounts — search, link, list, unlink, and the consent lifecycle that gates access. */
  dependents: {
    search: (data: { mobile: string; dateOfBirth: string }) =>
      fetchPatientApi<Array<{
        patientId: string; clinicId: string; clinicName?: string; maskedName: string;
        maskedMobile: string; dateOfBirth: string; isMinor: boolean; alreadyLinked: boolean;
      }>>('/api/patient/dependents/search', { method: 'POST', body: JSON.stringify(data) }),
    link: (data: {
      dependentPatientId?: string; dependentClinicId?: string; mobile: string; dateOfBirth: string;
      name?: string; gender?: string; relationshipType: 'CHILD' | 'PARENT' | 'SPOUSE' | 'SIBLING' | 'OTHER';
    }) =>
      fetchPatientApi<{
        dependentLinkId: string; dependentPatientId: string; dependentClinicId: string;
        status: string; isMinor: boolean; message?: string;
      }>('/api/patient/dependents/link', { method: 'POST', body: JSON.stringify(data) }),
    list: () =>
      fetchPatientApi<Array<{
        dependentLinkId: string; dependentPatientId: string; dependentClinicId: string;
        clinicName?: string; name: string; relationshipType: string; status: string; isMinor: boolean;
        activeConsentStatus?: string; activeConsentMethod?: string; validUntil?: string; createdAt: string;
      }>>('/api/patient/dependents'),
    unlink: (dependentLinkId: string, reason?: string) =>
      fetchPatientApi<void>(
        `/api/patient/dependents/${dependentLinkId}${reason ? `?reason=${encodeURIComponent(reason)}` : ''}`,
        { method: 'DELETE' }
      ),
    guardianConsent: (dependentLinkId: string, data: {
      guardianAttestedName: string; guardianAttestedRelationship: string;
      guardianSignatureDataUrl?: string; guardianDobConfirmed: string; acceptedAttestation: boolean;
    }) =>
      fetchPatientApi<{
        dependentConsentId: string; status: string; validFrom?: string;
        /** medopd-docs 96: a code was sent to the child's record's mobile; confirm with verifyOtp. */
        otpRequired?: boolean; maskedMobile?: string; otpExpiresInSeconds?: number;
      }>(
        `/api/patient/dependents/${dependentLinkId}/consent/guardian`,
        { method: 'POST', body: JSON.stringify(data) }
      ),
    sendOtp: (dependentLinkId: string) =>
      fetchPatientApi<{ dependentConsentId: string; status: string; maskedMobile: string; otpExpiresInSeconds: number }>(
        `/api/patient/dependents/${dependentLinkId}/consent/otp/send`,
        { method: 'POST' }
      ),
    verifyOtp: (dependentConsentId: string, otp: string) =>
      fetchPatientApi<{ error?: string; attemptsRemaining?: number; status?: string; validUntil?: string }>(
        '/api/patient/dependents/consent/otp/verify',
        { method: 'POST', body: JSON.stringify({ dependentConsentId, otp }) }
      ),
    revoke: (dependentConsentId: string, reason?: string) =>
      fetchPatientApi<void>(`/api/patient/dependents/consents/${dependentConsentId}/revoke`, {
        method: 'POST', body: JSON.stringify({ reason }),
      }),
  },

  records: {
    getVisits: () => fetchPatientApi<PatientVisitSummary[]>('/api/patient/visits'),
    getPrescriptions: () => fetchPatientApi<PatientPrescriptionSummary[]>('/api/patient/prescriptions'),
    /** Paged, newest first (medopd-docs 112). The unpaged calls stay for the dashboard. */
    getVisitsPage: (page: number, size: number) =>
      fetchPatientApi<PagedResponse<PatientVisitSummary>>(`/api/patient/visits${buildQS({ page, size })}`),
    /** One row per visit with its issued prescription and the doctor's approved summary (Health Records). */
    getVisitSummary: (visitId: string) =>
      fetchPatientApi<PatientVisitSummaryDetail>(`/api/patient/visit-records/${encodeURIComponent(visitId)}/summary`),
    getVisitRecordsPage: (page: number, size: number) =>
      fetchPatientApi<PagedResponse<PatientVisitRecord>>(`/api/patient/visit-records${buildQS({ page, size })}`),
    getPrescriptionsPage: (page: number, size: number) =>
      fetchPatientApi<PagedResponse<PatientPrescriptionSummary>>(`/api/patient/prescriptions${buildQS({ page, size })}`),
    getLabOrdersPage: (page: number, size: number) =>
      fetchPatientApi<PagedResponse<any>>(`/api/patient/lab-orders${buildQS({ page, size })}`),
    getLabUploadsPage: (page: number, size: number) =>
      fetchPatientApi<PagedResponse<any>>(`/api/patient/lab-uploads${buildQS({ page, size })}`),    /** The issued prescription as the clinic's signed PDF. */
    getPrescriptionPdf: (id: string) => fetchPatientGetFileApi(`/api/patient/prescriptions/${encodeURIComponent(id)}/pdf`),
    /** The doctor's approved plain-words summary of each visit (never the AI draft). */
    getVisitSummaries: () => fetchPatientApi<PatientVisitSummaryApproved[]>('/api/patient/visit-summaries'),
    getLabTrends: () => fetchPatientApi<Record<string, any[]>>('/api/patient/lab-trends'),
    getLabReports: () => fetchPatientApi<any[]>('/api/patient/lab-reports'),
    getCaseBrief: () => fetchPatientApi<string>('/api/patient/case-brief'),
    getLabOrders: () => fetchPatientApi<any[]>('/api/patient/lab-orders'),
    /** The signed report PDF of one released order (no AI text in it) — the stored snapshot the lab released. */
    getLabOrderReport: (orderId: string) => fetchPatientGetFileApi(`/api/patient/lab-orders/${encodeURIComponent(orderId)}/report`),
    /**
     * The clinician-approved plain-language interpretation of one released order. `summaryAvailable` is
     * false (and `awaitingClinicianReview` true) until a doctor signs it off — render the waiting state,
     * never any draft text. 404 when no interpretation exists at all.
     */
    getLabOrderAiSummary: (orderId: string) =>
      fetchPatientApi<PatientLabAiSummary>(`/api/patient/lab-orders/${encodeURIComponent(orderId)}/ai-summary`),

    /**
     * The patient's lab results with plain-language AI interpretation.
     *
     * Deliberately carries no confidence figure, no clinician follow-up and no clinical-register
     * summary — the server does not send them. `summaryAvailable` is false until a clinician has
     * signed the interpretation off; render the awaiting-review state rather than any AI text.
     */
    getLabAiSummaries: () =>
      fetchPatientApi<PatientLabAiSummary[]>('/api/patient/lab-ai-summaries'),
    getLabUploads: () => fetchPatientApi<any[]>('/api/patient/lab-uploads'),
    getLabDocumentPreview: (docId: string) =>
      fetchPatientApi<{ url: string }>(`/api/patient/lab-documents/${docId}/preview`),
  },
};

export const templatesModule = {
  /**
   * The server's real filters (PrescriptionTemplateController): category, doctorOnly (the caller's own
   * templates), clinicWideOnly. No filter = every active template in the clinic. There is no doctorId
   * param — the doctor is always the signed-in user.
   */
  list: (filters?: { category?: string; doctorOnly?: boolean; clinicWideOnly?: boolean }) =>
    fetchApi<PrescriptionTemplate[]>(
      `/api/doctor/prescription-templates${buildQS({ ...filters })}`
    ),
  get: (id: string) =>
    fetchApi<PrescriptionTemplate>(`/api/doctor/prescription-templates/${id}`),
  create: (data: CreateTemplateRequest) =>
    fetchApi<PrescriptionTemplate>('/api/doctor/prescription-templates', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  delete: (id: string) =>
    fetchApi<void>(`/api/doctor/prescription-templates/${id}`, { method: 'DELETE' }),
};

const BASE_ENH = '/api/doctor/templates/enhancements';

export const templateEnhancementsModule = {
  getFavorites: () =>
    fetchApi<PrescriptionTemplate[]>(`${BASE_ENH}/favorites`),
  getRecent: (days = 30) =>
    fetchApi<any[]>(`${BASE_ENH}/recent?days=${days}`),
  toggleFavorite: (templateId: string, favorite: boolean, pinned = false, pinOrder?: number) =>
    fetchApi<void>(`${BASE_ENH}/${templateId}/favorite`, {
      method: 'POST',
      body: JSON.stringify({ favorite, pinned, pinOrder }),
    }),
  recordUsage: (templateId: string) =>
    fetchApi<void>(`${BASE_ENH}/${templateId}/use`, { method: 'POST' }),
};

export const favoritesModule = {
  list: () => fetchApi<FavoriteMedication[]>('/api/doctor/favorite-medications'),
  top: (limit = 10) =>
    fetchApi<FavoriteMedication[]>(`/api/doctor/favorite-medications/top?limit=${limit}`),
  add: (data: AddFavoriteMedicationRequest) =>
    fetchApi<FavoriteMedication>('/api/doctor/favorite-medications', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  remove: (id: string) =>
    fetchApi<void>(`/api/doctor/favorite-medications/${id}`, { method: 'DELETE' }),
  /** Backend keys usage by medication name (DoctorFavoriteMedicationController POST /use/{medicationName}). */
  incrementUse: (medicationName: string) =>
    fetchApi<void>(`/api/doctor/favorite-medications/use/${encodeURIComponent(medicationName)}`, { method: 'POST' }),
};

export const diagnosesModule = {
  list: () => fetchApi<CommonDiagnosis[]>('/api/common-diagnoses'),
  search: (query: string) =>
    fetchApi<CommonDiagnosis[]>(`/api/common-diagnoses/search?q=${encodeURIComponent(query)}`),
  byCategory: (category: string) =>
    fetchApi<CommonDiagnosis[]>(`/api/common-diagnoses/category/${category}`),
};

export const createEnhancedPrescription = (data: EnhancedPrescriptionCreateRequest) =>
  fetchApi<Prescription>('/api/doctor/prescriptions', {
    method: 'POST',
    body: JSON.stringify(data),
  });
